import { createElement as rc, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { View, Text, Switch, styled, fromTheme } from 'lib_ui-primitives';
import useSearchElement from './useSearchElement';
import FormField from '../formElement/FormField';
import { useToggle } from '../../hooks';
import { metadata } from 'lib_ui-services';

// Always false so that label floats permanently (instead of overlapping the control).
const fieldEmpty = false;

const SwitchWithMargin = styled(Switch).attrs({ name: 'switch-with-margin' })`
    margin-left: ${({ size = 'sm' }) => fromTheme('switch', 'track', 'margin', size, 'left')};
    margin-right: ${({ size = 'sm' }) => fromTheme('switch', 'track', 'margin', size, 'right')};
    flex-shrink: 0;
`;
SwitchWithMargin.displayName = 'SwitchWithMargin';

const SwitchContainer = styled(View).attrs({ name: 'switch-container' })`
    padding-left: ${fromTheme('textPadding')};
    padding-top: ${fromTheme('textPadding')};
    margin-top: ${fromTheme('textMargin')};
    align-items: center;
    column-gap: ${({ size = 'sm' }) => fromTheme('switch', 'options', 'gap', size)};
`;
SwitchContainer.displayName = 'SwitchContainer';

const ToggleOptions = styled(Text).attrs({ name: 'toggle-options' })`
    color: ${({ selected }) => fromTheme('switch', 'options', 'color', selected ? 'selected' : 'unselected')};
    font-size: ${({ size = 'sm', selected }) =>
        fromTheme('switch', 'options', 'font', size, selected ? 'selected' : 'unselected', 'fontSize')};
    font-weight: ${({ size = 'sm', selected }) =>
        fromTheme('switch', 'options', 'font', size, selected ? 'selected' : 'unselected', 'fontWeight')};
    line-height: 1;
`;
ToggleOptions.displayName = 'ToggleOptions';

const DefaultFilterWrapper = filter => filter;
/**
 * @typedef {Object} Props
 * @property {Object} hNode
 */
/** @type {import('react').FC<Props>} */
function ToggleSearch(props) {
    const {
        hNode,
        hNode: { id, title, propertyName, toggleValues, neutralOption, includeUndefinedAsFalse, size = 'sm' },
        disabled,
        filterWrapper = DefaultFilterWrapper
    } = props || { hNode: {} };

    const valueToFilter = useCallback(
        state => {
            // If the state is a boolean, we can use it to filter
            if (typeof state === 'boolean') {
                const path = metadata.getPathToProperty(hNode);
                if (includeUndefinedAsFalse && !state) {
                    // if set to false and we want to include undefined as false
                    return filterWrapper({
                        [path]: { $ne: true }
                    });
                } else {
                    return filterWrapper({
                        [path]: { $eq: state }
                    });
                }
            }
            // Otherwise, the toggle is in the neutral state, clear the filter
            return {};
        },
        [filterWrapper, hNode, includeUndefinedAsFalse]
    );

    const { value, setValue, active, onFocus, onBlur } = useSearchElement(props, valueToFilter, neutralOption);
    const [toggleState, toggle] = useToggle(value, neutralOption);

    // Update the search element value when the toggle state changes
    useEffect(() => {
        setValue(toggleState);
    }, [toggleState, setValue]);

    // prettier-ignore
    return rc(FormField, { id, hNode, title, active, fieldEmpty },
        rc(SwitchContainer, {size},
            // Explcitly check for true or false to prevent the neutral option from being selected
            rc(ToggleOptions, { selected: value === false, size }, toggleValues[0]),
            rc(SwitchWithMargin, { id, value: value, onClick: toggle, disabled, name: propertyName, onFocus, onBlur, size, multistate: neutralOption != null && neutralOption.length }),
            rc(ToggleOptions, { selected: value === true, size }, toggleValues[1])
        )
    );
}

ToggleSearch.propTypes = {
    hNode: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        propertyName: PropTypes.string.isRequired,
        toggleValues: PropTypes.arrayOf(PropTypes.string).isRequired,
        neutralOption: PropTypes.string
    }).isRequired,
    disabled: PropTypes.bool
};

export default ToggleSearch;
